/**
 * Where this page reaches the SFU node the orchestrator assigned.
 *
 * The orchestrator answers with the node's internal address. Moved out of
 * `useSfuJoin` unchanged, so the Screen Share page resolves a node exactly the
 * way the meeting does — the same way video.html does.
 *
 * `page` is read only on the two branches that need it, as the inline version
 * did, so the common cases never touch `location`.
 */
export function resolveSfuNodeUrl(
  nodeUrl: string,
  orchestratorUrl: string,
  page?: { protocol: string; hostname: string },
): string {
  const nodeUrlParsed = new URL(nodeUrl)
  const nodeHost = nodeUrlParsed.hostname
  const isIp = /^\d+\.\d+\.\d+\.\d+$/.test(nodeHost)
  // Only loopback / RFC-1918 private / link-local addresses count as "local".
  // A public/raw IP must NOT downgrade to http (it would be a blocked insecure
  // request from an https page and an unintended prod behavior change).
  const isLocalIp =
    isIp &&
    /^(127\.|10\.|192\.168\.|169\.254\.|172\.(1[6-9]|2\d|3[01])\.)/.test(nodeHost)
  const isLocal = nodeHost === 'localhost' || isLocalIp
  if (isLocal) {
    // Local dev: talk to the assigned SFU node directly over http.
    // http://localhost|127.0.0.1 is an allowed secure context even from an https page.
    return `http://${nodeHost}:${nodeUrlParsed.port}`
  }
  if (nodeHost.includes('.') && !isIp) return `https://${nodeHost}`
  const loc = page ?? location
  if (loc.protocol === 'https:') return orchestratorUrl
  return `http://${loc.hostname}:${nodeUrlParsed.port}`
}

/**
 * Where this page reaches the node a join answer placed it on.
 *
 * `gatewayNodeUrl` — the cluster's one public SFU URL, the node named in its
 * path — is used as it comes: nodes that scale in and out have no public name
 * of their own, so there is nothing to rewrite, and the path must survive.
 * Without one (no gateway configured, local dev), `nodeUrl` resolves as above.
 */
export function sfuNodeUrlFromJoin(
  join: { nodeUrl: string; gatewayNodeUrl?: string },
  orchestratorUrl: string,
  page?: { protocol: string; hostname: string },
): string {
  if (join.gatewayNodeUrl) return join.gatewayNodeUrl
  return resolveSfuNodeUrl(join.nodeUrl, orchestratorUrl, page)
}
